import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  noIndex?: boolean;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile';
  lang?: string;
}

const DEFAULT_TITLE = 'LuvALL - Track Your Padel Matches & Improve Your Game';
const DEFAULT_DESCRIPTION =
  'Track matches, organise tournaments, climb the rankings. One fluid app for players, groups, and clubs who live for the game.';
const SITE_URL = 'https://luvall.app';
const DEFAULT_OG_IMAGE = `${SITE_URL}/luvall-logo.png`;

/**
 * Per-route SEO meta tags. Drop this near the top of any page component
 * to override the default <title> and meta description from index.html.
 */
export const SEO = ({
  title,
  description,
  canonical,
  noIndex = false,
  ogImage,
  ogType = 'website',
  lang = 'en',
}: SEOProps) => {
  const fullTitle = title ? `${title} · LuvALL` : DEFAULT_TITLE;
  const desc = description ?? DEFAULT_DESCRIPTION;
  const canonicalUrl = canonical
    ? canonical.startsWith('http')
      ? canonical
      : `${SITE_URL}${canonical}`
    : undefined;
  const image = ogImage ?? DEFAULT_OG_IMAGE;

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={image} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:site_name" content="LuvALL" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default SEO;
